import React, { useEffect, useState } from "react";
import logo from "../assets/logo.png";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import PdfViewer from "./PdfViewer";
import pdfIcon from "../assets/pdf.png";
import wordIcon from "../assets/word.png";
import excelIcon from "../assets/excel.png";
import imageIcon from "../assets/image.png";
import '../styles/fonts.css';

const folders = [
  { id: "corporate-legal", name: "Corporate & Legal" },
  { id: "financial-strategy", name: "Financial" },
  { id: "market-strategy", name: "Market & Strategy" },
  { id: "product-tech", name: "Product & Technology" },
  { id: "team", name: "Team" },
  { id: "traction-validation", name: "Traction & Validation" }
];

const getFileIcon = (filename) => {
  const ext = filename.split(".").pop().toLowerCase();
  if (ext === "pdf") return pdfIcon;
  if (["doc", "docx"].includes(ext)) return wordIcon;
  if (["xls", "xlsx", "csv"].includes(ext)) return excelIcon;
  if (["png", "jpg", "jpeg", "gif", "webp"].includes(ext)) return imageIcon;
  return pdfIcon; // fallback
};

const FileExplorer = () => {
  const [activeFolder, setActiveFolder] = useState(null);
  const [hoveredFolder, setHoveredFolder] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  const [prefix, setPrefix] = useState("");
  const [subFolders, setSubFolders] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const openFile = (file) => {
    const name = file.name.toLowerCase();
    const ext = name.split('.').pop();

    if (ext === 'pdf') {
      setPdfUrl(file.url);
      return;
    }

    // Use Office viewer for Office docs/spreadsheets
    if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)) {
      const officeViewer = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(file.url)}`;
      window.open(officeViewer, '_blank', 'noopener,noreferrer');
      return;
    }

    // Everything else: open the file URL
    window.open(file.url, '_blank', 'noopener,noreferrer');
  };

  // Load files/folders for a given prefix
  async function load(pfx = "") {
    setLoading(true);
    setError(null);
    
    try {
      // Normalize prefix: remove leading slashes
      const normalizedPrefix = pfx.replace(/^\/+/, "");
      
      console.log(`🔍 Loading prefix: "${normalizedPrefix}"`);
      
      const params = new URLSearchParams({ prefix: normalizedPrefix });
      const url = `${process.env.REACT_APP_API_BASE}/api/files?${params}`;
      
      console.log(`📡 Fetching: ${url}`);
      
      const res = await fetch(url);
      
      if (!res.ok) {
        throw new Error(`Server error: ${res.status} ${res.statusText}`);
      }
      
      const data = await res.json();
      
      console.log(`✅ Received:`, data);

      setPrefix(data.prefix || normalizedPrefix);
      setSubFolders(Array.isArray(data.folders) ? data.folders : []);
      setFileList(Array.isArray(data.files) ? data.files : []);
    } catch (err) {
      console.error("❌ Load failed:", err);
      setError(err.message);
      setSubFolders([]);
      setFileList([]);
    } finally {
      setLoading(false);
    }
  }

  // Reset to top-level folder view
  function resetToTop() {
    setActiveFolder(null);
    setPrefix("");
    setSubFolders([]);
    setFileList([]);
    setError(null);
  }

  // Build breadcrumb trail from current prefix
  const buildBreadcrumbs = () => {
    if (!activeFolder || !prefix) return [];
    
    // Remove the base folder ID from prefix to get subpath
    const basePath = `${activeFolder.id}/`;
    const subPath = prefix.replace(basePath, "");
    
    if (!subPath) return [];
    
    // Split into parts and build cumulative paths
    const parts = subPath.split("/").filter(Boolean);
    return parts.map((part, index) => ({
      name: part,
      path: basePath + parts.slice(0, index + 1).join("/") + "/"
    }));
  };

  const breadcrumbs = buildBreadcrumbs();

  return (
    <div style={styles.page}>
      {/* Hero Section */}
      <div style={styles.hero}>
        <div style={styles.overlay}>
          <button
            style={{ ...styles.button, ...styles.logoutButton }}
            onClick={() => signOut(auth)}
          >
            Logout
          </button>

          <img src={logo} alt="V-Quest Logo" style={styles.logoImg} />

          <div style={styles.folderContainer}>
            {activeFolder ? (
              <>
                <div style={styles.focusedFolder}>{activeFolder.name}</div>
                <button style={styles.backButton} onClick={resetToTop}>
                  ← Back to All Folders
                </button>

                {/* Breadcrumb Navigation */}
                {breadcrumbs.length > 0 && (
                  <div style={styles.breadcrumbs}>
                    <a
                      href="#/"
                      onClick={(e) => {
                        e.preventDefault();
                        load(`${activeFolder.id}/`);
                      }}
                      style={styles.breadcrumbLink}
                    >
                      {activeFolder.name}
                    </a>
                    {breadcrumbs.map((crumb, i) => (
                      <span key={i}>
                        <span style={styles.breadcrumbSeparator}>/</span>
                        <a
                          href="#/"
                          onClick={(e) => {
                            e.preventDefault();
                            load(crumb.path);
                          }}
                          style={styles.breadcrumbLink}
                        >
                          {crumb.name}
                        </a>
                      </span>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div style={styles.folderRow}>
                {folders.map((folder) => (
                  <div
                    key={folder.id}
                    style={{
                      ...styles.folder,
                      ...(hoveredFolder === folder.id ? styles.folderHover : {})
                    }}
                    onClick={() => {
                      setActiveFolder(folder);
                      load(`${folder.id}/`);
                    }}
                    onMouseEnter={() => setHoveredFolder(folder.id)}
                    onMouseLeave={() => setHoveredFolder(null)}
                  >
                    {folder.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* File List Section */}
      <div style={styles.bottomWhite}>
        {/* Intro text when nothing is selected */}
        {!activeFolder && (
          <div style={styles.intro}>
            <h2 style={styles.sectionTitle}>Welcome to the ηezo Data Vault</h2>
            <p style={styles.introText}>
              This is your secure access point to everything behind ηezo — our vision,
              traction, and the numbers that prove it. Inside, you’ll find our corporate
              and legal foundation, financial performance, market strategy, technology
              roadmap, team structure, and validation milestones. We’ve built this vault
              to give investors full transparency into how ηezo is redefining design
              efficiency — combining cost, carbon, and weight analytics to make better
              buildings possible.
            </p>
          </div>
        )}
        {activeFolder && (
          <>
            {loading && <div style={styles.loading}>Loading...</div>}
            {error && <div style={styles.error}>Error: {error}</div>}
            
            {!loading && !error && (
              <>
                {/* Subfolders */}
                {subFolders.length > 0 && (
                  <>
                    <h2 style={styles.sectionTitle}>📁 Folders</h2>
                    <div style={styles.fileGrid}>
                      {subFolders.map((folder) => (
                        <div
                          key={folder.path}
                          style={styles.fileTile}
                          onClick={() => load(folder.path)}
                          title={folder.path}
                        >
                          <div style={{ fontSize: "2.5rem", marginBottom: ".5rem" }}>
                            📁
                          </div>
                          <div style={styles.fileLabel}>{folder.name}</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Files */}
                <h2
                  style={{
                    ...styles.sectionTitle,
                    marginTop: subFolders.length ? "2rem" : 0
                  }}
                >
                  📄 Files
                </h2>
                <div style={styles.fileGrid}>
                  {fileList.map((file) => (
                    <div
                      key={file.path}
                      style={styles.fileTile}
                      onClick={() => openFile(file)}   
                      title={file.path}
                    >
                      <img
                        src={getFileIcon(file.name)}
                        alt="File icon"
                        style={styles.fileIcon}
                      />
                      <div style={styles.fileLabel}>{file.name}</div>
                    </div>
                  ))}
                  {fileList.length === 0 && subFolders.length === 0 && (
                    <div style={styles.emptyState}>
                      This folder is empty.
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
        <PdfViewer url={pdfUrl} onClose={() => setPdfUrl(null)} />
      </div>
    </div>
  );
};


const styles = {
  page: {
    fontFamily: "'Gotham Rounded', sans-serif",
    margin: 0,
    padding: 0
  },
  button: {
    fontFamily: "'Gotham Rounded', sans-serif",
    padding: "15px 30px",
    fontSize: "1.2em",
    border: "none",
    cursor: "pointer",
    borderRadius: "8px",
    transition: "all 0.3s ease",
    boxShadow:
      "rgba(50, 50, 93, 0.9) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px",
    backgroundColor: "#ffffff",
    color: "#243c4c"
  },
  logoutButton: {
    position: "absolute",
    top: "20px",
    right: "20px"
  },
  backButton: {
    fontFamily: "'Gotham Rounded', sans-serif",
    marginTop: "1rem",
    padding: "15px 30px",
    fontSize: "1.2em",
    border: "none",
    cursor: "pointer",
    borderRadius: "8px",
    transition: "all 0.3s ease",
    boxShadow:
      "rgba(50, 50, 93, 0.9) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px",
    backgroundColor: "#ffffff",
    color: "#243c4c"
  },
  hero: {
    position: "relative",
    height: "60vh",
    overflow: "hidden",
    background: "linear-gradient(135deg, #E6F3F7 0%, #B8E0D2 100%)",
    backgroundSize: "cover",
    backgroundPosition: "center",
    marginBottom: "5rem"
  },
  overlay: {
    position: "relative",
    zIndex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    color: "#fff"
  },
  logoImg: {
    width: "400px",
    marginBottom: "5rem"
  },
  folderRow: {
    display: "flex",
    gap: "2rem",
    flexWrap: "wrap",
    justifyContent: "center"
  },
  folder: {
    backgroundColor: "#ffffff",
    color: "#217867",
    padding: "1.5rem 2rem",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    fontSize: "1.2rem",
    fontWeight: "bold",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    cursor: "pointer"
  },
  folderHover: {
    transform: "scale(1.05)",
    boxShadow: "0 6px 18px rgba(0, 0, 0, 0.25)"
  },
  focusedFolder: {
    backgroundColor: "#ffffff",
    color: "#217867",
    padding: "1.8rem 3rem",
    borderRadius: "10px",
    fontSize: "1.6rem",
    fontWeight: "bold",
    boxShadow: "0 6px 18px rgba(0, 0, 0, 0.2)",
    marginBottom: "1rem"
  },
  folderContainer: {
    minHeight: "180px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "start"
  },
  fileGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: "2rem",
    marginTop: "1rem"
  },
  fileTile: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: "140px",
    cursor: "pointer",
    padding: "1rem",
    borderRadius: "10px",
    backgroundColor: "#f9f9f9",
    boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    textAlign: "center"
  },
  fileTileHover: {
    transform: "scale(1.05)",
    boxShadow: "0 4px 10px rgba(0,0,0,0.2)"
  },
  fileIcon: {
    width: "40px",
    height: "40px",
    marginBottom: "0.5rem",
    objectFit: "contain"
  },
  fileLabel: {
    fontSize: "0.9rem",
    textAlign: "center",
    color: "#243c4c",
    wordBreak: "break-word"
  },
  // NOTE: you had a duplicate backButton style further down;
  // keep one definition (the larger white back button above).
  bottomWhite: {
    backgroundColor: "#ffffff",
    padding: "2rem 2rem 1rem 2rem",
    marginTop: "-6rem"
  },
  sectionTitle: {
    color: "#217867",
    fontSize: "1.5rem",
    marginBottom: "1rem"
  },
  fileList: {
    listStyle: "none",
    padding: 0,
    margin: 0
  },
  fileItem: {
    padding: "0.5rem 0",
    color: "#333"
  },
  breadcrumbs: {
    marginTop: "1rem",
    color: "#243c4c",
    fontSize: "1rem"
  },
  breadcrumbLink: {
    color: "#217867",
    textDecoration: "none",
    fontWeight: "500",
    cursor: "pointer"
  },
  breadcrumbSeparator: {
    margin: "0 0.5rem",
    color: "#6b7b86"
  },
  loading: {
    textAlign: "center",
    padding: "2rem",
    color: "#217867",
    fontSize: "1.2rem"
  },
  error: {
    textAlign: "center",
    padding: "2rem",
    color: "#d32f2f",
    fontSize: "1.1rem"
  },
  emptyState: {
    color: "#6b7b86",
    fontStyle: "italic"
  },
  intro: {
  maxWidth: "900px",
  margin: "0 auto",
  padding: "0.5rem 0 1rem 0",
  textAlign: "center"
},
introText: {
  fontSize: "1.05rem",
  lineHeight: 1.7,
  color: "#243c4c",
  margin: 0
}

};

export default FileExplorer;
